import * as React from "react";
import { inject, observer } from "mobx-react";
import { set } from "mobx";
import styled from "styled-components";
import { Switch } from "@lib/components";
import { MobxComponent } from "../../../../../mobx/components";
import { UI } from "../../../../../core/ui";
import { IconGridView, IconListView } from "../../../../components/v2/icons";
import { KitchenStationEditor, blankStation } from "./editor";
import { StationIcon } from "./icons";

const Page = styled.div`
	max-width: 1180px;
`;

const Title = styled.h1`
	margin: 0 0 18px;
	font-size: 22px;
	font-weight: 600;
`;

const Toolbar = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 18px;
	flex-wrap: wrap;
`;

const Search = styled.input`
	flex: 1 1 240px;
	max-width: 280px;
	height: 38px;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	padding: 0 12px;
	background: #fff;
	font-size: 14px;
`;

const Spacer = styled.div`
	flex: 1;
`;

const IconBtn = styled.button<{ active?: boolean }>`
	width: 36px;
	height: 36px;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	background: ${({ active }) => (active ? "#f3f4f6" : "#fff")};
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	padding: 0;
`;

const AddBtn = styled.button`
	height: 38px;
	border: 0;
	border-radius: 6px;
	padding: 0 14px;
	background: ${({ theme }) => theme.colors.primary};
	color: ${({ theme }) => theme.colors.primary_text || "#fff"};
	font-weight: 600;
	cursor: pointer;
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
	gap: 16px;
`;

const Card = styled.div`
	background: #fff;
	border: 1px solid #eceff3;
	border-radius: 10px;
	display: flex;
	flex-direction: column;
	min-height: 168px;
`;

const CardBody = styled.div`
	padding: 16px 16px 8px;
	flex: 1;
`;

const Name = styled.div`
	margin-top: 10px;
	font-weight: 600;
`;

const Description = styled.div`
	margin-top: 6px;
	color: #6b7280;
	font-size: 13px;
	min-height: 18px;
`;

const CardFooter = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 16px;
	border-top: 1px solid #f1f3f5;
	button:last-child {
		margin-left: auto;
	}
`;

const EditBtn = styled.button`
	border: 0;
	background: transparent;
	color: #4b4f5e;
	font-weight: 600;
	cursor: pointer;
`;

const List = styled.div`
	background: #fff;
	border: 1px solid #eceff3;
	border-radius: 10px;
`;

const Row = styled.div`
	display: grid;
	grid-template-columns: 220px 1fr auto auto;
	gap: 12px;
	align-items: center;
	padding: 14px 16px;
	border-bottom: 1px solid #f1f3f5;
	&:last-child {
		border-bottom: 0;
	}
	@media (max-width: 720px) {
		grid-template-columns: 1fr;
	}
`;

const RowName = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	font-weight: 600;
`;

const Empty = styled.div`
	padding: 28px;
	text-align: center;
	color: #6b7280;
	background: #fff;
	border: 1px dashed #e5e7eb;
	border-radius: 10px;
`;

const Note = styled.p`
	color: #6b7280;
`;

interface State {
	query: string;
	view: "cards" | "list";
	editing: T.Schema.Restaurant.KdsStation | null;
	saving: boolean;
}

@inject("store") @observer
export class RestaurantKds extends MobxComponent<{}, State> {
	constructor(props: {}) {
		super(props);
		this.state = { query: "", view: "cards", editing: null, saving: false };
	}

	persist = async (stations: T.Schema.Restaurant.KdsStation[], notify: boolean) => {
		const { store } = this.injected;
		const r = store.restaurant;
		if (!r) return false;
		const previous = r.kds;
		const next: T.Schema.Restaurant.RestaurantKdsSettings = {
			enabled: r.kds?.enabled === true,
			stations,
		};
		this.setState({ saving: true });
		set(r, { kds: next });
		try {
			const res = await store.api.restaurant_update({
				_id: r._id,
				update: { $set: { kds: next } },
			});
			if (res.outcome) {
				set(r, { kds: previous });
				UI.notification.error(res.message || "Failed to save kitchen station");
				return false;
			}
			if (notify) UI.notification.success("Kitchen station saved");
			return true;
		} catch (e) {
			set(r, { kds: previous });
			UI.notification.error("Failed to save kitchen station");
			return false;
		} finally {
			this.setState({ saving: false });
		}
	};

	saveStation = async (station: T.Schema.Restaurant.KdsStation) => {
		const name = station.name.trim();
		if (!name) {
			UI.notification.error("Enter a station name");
			return;
		}
		const r = this.injected.store.restaurant!;
		const stations = [...(r.kds?.stations || [])];
		const next = { ...station, name };
		const index = stations.findIndex(item => item.id === station.id);
		if (index === -1) stations.push(next);
		else stations[index] = next;
		const ok = await this.persist(stations, true);
		if (ok) this.setState({ editing: null });
	};

	toggleActive = (station: T.Schema.Restaurant.KdsStation, active: boolean) => {
		const stations = (this.injected.store.restaurant?.kds?.stations || []).map(item =>
			item.id === station.id ? { ...item, active } : item
		);
		this.persist(stations, false);
	};

	renderActive(station: T.Schema.Restaurant.KdsStation) {
		return (
			<>
				<Switch
					id={`kds-active-${station.id}`}
					checked={station.active !== false}
					onChange={e => this.toggleActive(station, e.target.checked)}
				/>
				<span>Active</span>
			</>
		);
	}

	render() {
		const r = this.injected.store.restaurant;
		if (!r) return null;
		const enabled = r.kds?.enabled === true;
		const { query, view, editing, saving } = this.state;
		const q = query.trim().toLowerCase();
		const stations = (r.kds?.stations || []).filter(station => {
			if (!q) return true;
			return station.name.toLowerCase().includes(q) || (station.description || "").toLowerCase().includes(q);
		});

		return (
			<Page>
				<Title>Kitchen Stations</Title>
				{!enabled && (
					<Note>Turn on Kitchen Display under Settings, Integrations to manage stations.</Note>
				)}
				{enabled && (
					<>
						<Toolbar>
							<Search
								value={query}
								placeholder="Search Keyword"
								onChange={e => this.setState({ query: e.target.value })}
							/>
							<Spacer />
							<IconBtn type="button" active={view === "cards"} aria-label="Cards" onClick={() => this.setState({ view: "cards" })}>
								<IconGridView />
							</IconBtn>
							<IconBtn type="button" active={view === "list"} aria-label="List" onClick={() => this.setState({ view: "list" })}>
								<IconListView />
							</IconBtn>
							<AddBtn type="button" onClick={() => this.setState({ editing: blankStation() })}>+ Add Kitchen Station</AddBtn>
						</Toolbar>
						{stations.length === 0 && <Empty>No kitchen stations yet.</Empty>}
						{stations.length > 0 && view === "cards" && (
							<Grid>
								{stations.map(station => (
									<Card key={station.id}>
										<CardBody>
											<StationIcon name={station.icon} />
											<Name>{station.name}</Name>
											<Description>{station.description}</Description>
										</CardBody>
										<CardFooter>
											{this.renderActive(station)}
											<EditBtn type="button" onClick={() => this.setState({ editing: station })}>Edit</EditBtn>
										</CardFooter>
									</Card>
								))}
							</Grid>
						)}
						{stations.length > 0 && view === "list" && (
							<List>
								{stations.map(station => (
									<Row key={station.id}>
										<RowName>
											<StationIcon name={station.icon} />
											{station.name}
										</RowName>
										<Description style={{ marginTop: 0, minHeight: 0 }}>{station.description}</Description>
										<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
											{this.renderActive(station)}
										</div>
										<EditBtn type="button" onClick={() => this.setState({ editing: station })}>Edit</EditBtn>
									</Row>
								))}
							</List>
						)}
						{editing && (
							<KitchenStationEditor
								key={editing.id}
								station={editing}
								isNew={!(r.kds?.stations || []).some(item => item.id === editing.id)}
								store={this.injected.store}
								saving={saving}
								onClose={() => this.setState({ editing: null })}
								onSave={this.saveStation}
							/>
						)}
					</>
				)}
			</Page>
		);
	}
}
